import { ShipmentPackagingType } from "app/pages/shipments/types/domain";
import { CargoGroupType } from "features/cargo-groups/api/types";
import i18next from "i18next";
import { IncotermsEnum } from "types/Incoterms";
import { RoutingTypeEnum } from "types/RoutingType";
import { TrailerSizeEnum } from "types/TrailerSize";
import { TrailerTypeEnum } from "types/TrailerType";
import { UnloadingDetailsEnum } from "types/UnloadingDetails";
import { z } from "zod";

const addressSchema = z.object({
  id: z.number().optional(),
  name: z.string().nullable().optional(),
  address_1: z.string().nullable().optional(),
  address_2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  zip: z.string().nullable().optional(),
  country_code: z.string().nullable().optional(),
  country_name: z.string().nullable().optional(),
  province_code: z.string().nullable().optional(),
  province_name: z.string().nullable().optional(),
  google_place_id: z.string().nullable().optional(),
  google_place_description: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
});

const stopSchema = z.object({
  address: addressSchema.refine((val) => val != null, {
    message: i18next.t("quoteRequest:LocationStep.addressRequired"),
  }),
  start: z.string().optional().nullable(),
  end: z.string().optional().nullable(),
});

export const locationSchema = z.object({
  stops: stopSchema.array().length(2),
  incoterms: z.nativeEnum(IncotermsEnum),
  routing_type: z.nativeEnum(RoutingTypeEnum),
  cargo_ready_date: z.string(),
  target_delivery_date: z.string().optional(),
  unloading_details: z.nativeEnum(UnloadingDetailsEnum),
});

export type StopSchema = z.infer<typeof stopSchema>;
export type LocationSchema = z.infer<typeof locationSchema>;

export const baseCargoGroup = z.object({
  packaging_type: z.nativeEnum(ShipmentPackagingType),
  cargo_group_type: z.nativeEnum(CargoGroupType),
  count: z.number().optional(),
  commodities: z.string().optional(),
  weight_per_count: z.number().optional(),
  height_per_count: z.number().optional(),
  length_per_count: z.number().optional(),
  width_per_count: z.number().optional(),
  volume_per_count: z.number().optional(),
  is_stackable: z.boolean().optional(),
  equipment_type: z.nativeEnum(TrailerTypeEnum),
  equipment_size: z.nativeEnum(TrailerSizeEnum),
  min_temp: z.number().optional().nullable(),
  max_temp: z.number().optional().nullable(),
  requires_genset: z.boolean().optional(),
});

type CreateCargoGroup = z.infer<typeof baseCargoGroup> & {
  handling_units?: CreateCargoGroup[];
  packing_units?: CreateCargoGroup[];
  cargo_unit_id?: number;
  handling_unit_id?: number;
};

const createCargoGroup: z.ZodType<CreateCargoGroup> = baseCargoGroup.extend({
  handling_units: z.lazy(() => createCargoGroup.array()).optional(),
  packing_units: z.lazy(() => createCargoGroup.array()).optional(),
  cargo_unit_id: z.number().optional(),
  handling_unit_id: z.number().optional(),
});

export type CargoGroup = z.infer<typeof createCargoGroup>;

export const shipmentDetailsSchema = locationSchema.and(
  z.object({
    deadline_respond_at: z.string().optional(),
    reference_number: z.string().optional(),
    internal_reference_number: z.string().optional().nullable(),
    hide_internal_ref_till_dispatch: z.boolean().optional(),
    is_hazardous: z.boolean(),
    hazardous_notes: z.string().optional(),
    notes: z.string().optional(),
    include_customs_clearance: z.boolean(),
    is_overweight: z.boolean(),
    include_cargo_insurance: z.boolean(),
    cargo_groups: z.array(createCargoGroup).min(1),
  })
);

export const partnersSchema = shipmentDetailsSchema.and(
  z.object({
    partners: z.object({ contact_id: z.number() }).array().min(1, { message: "At least one partner must be selected" }),
  })
);

export type PartnersScheam = z.infer<typeof partnersSchema>;

export const teamSchema = partnersSchema.and(
  z.object({
    teammates: z.object({ contact_id: z.number() }).array().optional(),
  })
);

export type TeamSchema = z.infer<typeof teamSchema>;

export type FinalSchema = TeamSchema;
